import Wrapper from './wrapper';
import { IWrapper } from './types';
import {
  PaginatedPackagesResponse,
  Package as IPackage,
  IPartialPackage,
  PaginatedPackageEntriesResponse,
  Packager,
  PackageEntryStatus,
  PackagePreviewResponse,
  PackageTaskResponse,
  ValidationReportRow,
} from '../types/packager-types';

export interface IPackageEntriesParams {
  advanced?: boolean;
  include_transitions?: boolean;
  ordering?: string;
  limit?: number;
  offset?: number;
  package?: string;
  // Loosened to plain strings: completeness drill-downs forward comma-joined
  // CSV values (an IN filter, e.g. "INCLUDED,TRANSMISSION_SUCCESSFUL") that the
  // client passes through verbatim.
  status_in_package?: string;
  document?: string;
  missing_extraction?: string;
}

export interface IPackagesListParams {
  advanced?: boolean;
  include_transitions?: boolean;
  ordering?: string;
  limit?: number;
  offset?: number;
}

class Packages {
  wrapper: IWrapper;
  /**
   * @param {Wrapper} wrapper
   */
  constructor(wrapper: Wrapper) {
    this.wrapper = wrapper;
  }

  delete(id: string): Promise<void> {
    return this.wrapper.client.delete(`data-pools/${this.wrapper.dataPoolId}/packages/${id}`);
  }

  download(packagerId: string, packageId: string): Promise<string> {
    return this.wrapper.client.post(
      `data-pools/${this.wrapper.dataPoolId}/packagers/${packagerId}/download-package`,
      { package_id: packageId }
    );
  }

  reRender(packageId: string): Promise<PackageTaskResponse> {
    return this.wrapper.client.post(
      `data-pools/${this.wrapper.dataPoolId}/packages/${packageId}/re-render/`
    );
  }

  createFromPackage(packageId: string): Promise<PackageTaskResponse> {
    return this.wrapper.client.post(
      `data-pools/${this.wrapper.dataPoolId}/packages/${packageId}/create-from-package/`
    );
  }

  preview(packagerId: string, packageId: string): Promise<PackagePreviewResponse> {
    return this.wrapper.client.post(
      `data-pools/${this.wrapper.dataPoolId}/packagers/${packagerId}/preview-package`,
      { package_id: packageId }
    );
  }

  retrieve(params?: IPackagesListParams): Promise<PaginatedPackagesResponse> {
    // include_transitions=true populates available_transitions on each package — used to gate the edit-status dropdown.
    const merged: IPackagesListParams = {
      include_transitions: true,
      ordering: '-created_at',
      ...params,
    };
    return this.wrapper.client.get(`data-pools/${this.wrapper.dataPoolId}/packages`, {
      params: merged,
    });
  }

  // Single-package retrieve. advanced=true (matching the single-entry GET) gives
  // obfuscated-id handling and the detail-only `validation` block, which the list
  // endpoint omits.
  getById(packageId: string): Promise<IPackage> {
    return this.wrapper.client.get(
      `data-pools/${this.wrapper.dataPoolId}/packages/${packageId}`,
      { params: { advanced: true } }
    );
  }

  // When extraction_batch_ids or document_ids are supplied, the resulting package terminates at
  // DRAFT regardless of the packager's delivery schedule (ad-hoc preview run). Backend rejects
  // non-string entries and STREAM packagers with a 400.
  create(
    packagerId: string,
    body?: { extraction_batch_ids?: string[]; document_ids?: string[] }
  ): Promise<Packager> {
    return this.wrapper.client.post(
      `data-pools/${this.wrapper.dataPoolId}/packagers/${packagerId}/create-package`,
      body
    );
  }

  packageEntries(params: IPackageEntriesParams): Promise<PaginatedPackageEntriesResponse> {
    return this.wrapper.client.get(`data-pools/${this.wrapper.dataPoolId}/packageentries`, {
      params,
    });
  }

  updatePackageEntry(
    id: string,
    data: { status_in_package: PackageEntryStatus; exclusion_reason?: string | null }
  ): Promise<void> {
    return this.wrapper.client.patch(
      `data-pools/${this.wrapper.dataPoolId}/packageentries/${id}/?advanced=true`,
      data
    );
  }

  // Single entry retrieval not required; rely on list queries

  update(packageId: string, data: IPartialPackage): Promise<Packager> {
    return this.wrapper.client.patch(
      `data-pools/${this.wrapper.dataPoolId}/packages/${packageId}`,
      data
    );
  }

  // On-demand, per-issue validation report (one row per issue). Not embedded in the
  // package detail's count-only `validation` block — fetched only when the user opens
  // the report. Already scoped to this package's own data on the API.
  validationReport(packageId: string): Promise<ValidationReportRow[]> {
    return this.wrapper.client.post(
      `data-pools/${this.wrapper.dataPoolId}/packages/${packageId}/validation-report`,
      { format: 'json', download: false }
    );
  }

  triggerDelivery(packagerId: string, packageId: string): Promise<void> {
    return this.wrapper.client.post(
      `data-pools/${this.wrapper.dataPoolId}/packagers/${packagerId}/trigger-delivery`,
      { package_id: packageId }
    );
  }
}

export default Packages;
